import {
  ButtonArrowState,
  Button as TitleTypography,
  TertiaryButton,
  PrimaryButton,
  SecondaryButton,
  useThemeContext,
} from '@huggg/react-components';
import classnames from 'classnames';
import SuccessIcon from 'components/SuccessIcon/SuccessIcon';
import { Dialog } from 'components/ui';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  isSupplierError,
  supplierVoucherError,
  tilloVoucherError,
  voucherRedemptionError,
} from 'utilities/errorHandling';
import { interpolate } from 'utilities/interpolate';
import styles from './card.module.scss';

const CardContent = ({
  brandAssets,
  brandName,
  isBlueHuggg,
  voucherAmount,
  formattedVoucherExpiry,
  processedVoucherLabel,
  isWaitingForFulfil,
  onClaimCard,
  voucherError,
  isTilloVoucher,
  hugggBrandName,
  ctaTitle,
  confirmClaim,
  showSuccessTick,
  successMessage,
}) => {
  const { operator } = useThemeContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const itemType = isBlueHuggg ? 'voucher' : 'gift card';
  const capitalizedItemType =
    itemType.charAt(0).toUpperCase() + itemType.slice(1);

  // A supplier block (e.g. MBLS/Greggs) is not CS-fixable — show "try again
  // later" instead of the contact-CS message. Keyed on the error code so other
  // error classes are unaffected. if/else avoids a nested ternary.
  let errorMessage;
  if (isSupplierError(voucherError)) {
    errorMessage = supplierVoucherError();
  } else if (isTilloVoucher) {
    errorMessage = tilloVoucherError(hugggBrandName, operator);
  } else {
    errorMessage = voucherRedemptionError(operator);
  }
  const shouldShowClaimButton =
    Boolean(processedVoucherLabel) && !(voucherError && isTilloVoucher);

  const handleClaim = () => {
    if (!confirmClaim) {
      onClaimCard();
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmClaim = () => {
    onClaimCard();
    setShowConfirmModal(false);
  };

  const handleCancelClaim = () => {
    setShowConfirmModal(false);
  };

  const confirmTitle =
    confirmClaim?.title ?? dictionary.cardReveal.confirmTitle;
  const confirmButtonText =
    confirmClaim?.confirmButtonText ?? dictionary.cardReveal.confirmButtonText;
  const cancelButtonText =
    confirmClaim?.cancelButtonText ?? dictionary.cardReveal.cancelButtonText;

  return (
    <div
      style={{
        gap: '35px',
        marginTop: '35px',
      }}
      className={styles.columnCenter}
    >
      <TitleTypography className={classnames(styles.title)}>
        Your {brandName} {itemType} is ready!
      </TitleTypography>
      {brandAssets.giftCardUrl && (
        <img
          src={brandAssets.giftCardUrl}
          alt={'gift-card'}
          className={styles.cardImage}
        />
      )}
      <TitleTypography className={classnames(styles.title)}>
        {capitalizedItemType} value:{' '}
        <span style={{ fontWeight: 'bold' }}>{voucherAmount}</span>
      </TitleTypography>
      {formattedVoucherExpiry && (
        <p className={styles.voucherExpiry}>
          {interpolate(
            dictionary.voucherExpiry,
            '[expiry_date]',
            <>{formattedVoucherExpiry}</>,
          )}
        </p>
      )}
      {showSuccessTick && (
        <div className={styles.successIconWrapper}>
          <div className={styles.successIcon}>
            <SuccessIcon alt="Success" scale={1} />
          </div>
        </div>
      )}
      {ctaTitle && (
        <TitleTypography
          className={classnames(styles.title)}
          style={{ fontWeight: 500 }}
        >
          {ctaTitle}
        </TitleTypography>
      )}
      {successMessage && (
        <p className={styles.successMessage}>{successMessage}</p>
      )}
      {shouldShowClaimButton && (
        <div
          style={{
            gap: '15px',
          }}
          className={styles.columnCenter}
        >
          <TertiaryButton
            loading={isWaitingForFulfil}
            className={styles.voucherButton}
            onClick={handleClaim}
            data-test-id="button-visit-external"
            label={processedVoucherLabel}
            arrow={ButtonArrowState.None}
          />
        </div>
      )}

      {confirmClaim && (
        <Dialog
          visible={showConfirmModal}
          onClose={handleCancelClaim}
          disableOverlayClick={true}
          showCloseButton={false}
          contentClassName={styles.confirmClaimContent}
          title={confirmTitle}
        >
          <div className={styles.confirmClaimButtons}>
            <PrimaryButton
              className={styles.confirmClaimConfirm}
              onClick={handleConfirmClaim}
              data-test-id="confirm-claim-dialog-confirm"
              label={confirmButtonText.toUpperCase()}
            />
            <SecondaryButton
              onClick={handleCancelClaim}
              label={cancelButtonText.toUpperCase()}
              arrow={ButtonArrowState.Backwards}
              className={styles.confirmClaimCancel}
              data-test-id="confirm-claim-dialog-cancel"
            />
          </div>
        </Dialog>
      )}

      {voucherError && <div className={styles.error}>{errorMessage}</div>}
    </div>
  );
};

CardContent.propTypes = {
  brandAssets: PropTypes.object.isRequired,
  brandName: PropTypes.string,
  voucherAmount: PropTypes.string,
  formattedVoucherExpiry: PropTypes.string,
  processedVoucherLabel: PropTypes.string,
  isWaitingForFulfil: PropTypes.bool.isRequired,
  onClaimCard: PropTypes.func.isRequired,
  voucherError: PropTypes.object,
  isTilloVoucher: PropTypes.bool,
  hugggBrandName: PropTypes.string,
  ctaTitle: PropTypes.string,
  confirmClaim: PropTypes.shape({
    title: PropTypes.string,
    confirmButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string,
  }),
  showSuccessTick: PropTypes.bool,
  successMessage: PropTypes.string,
};

export default CardContent;
